import {
  ChatAgentResponseChunkType,
  UiAssistantMessage,
  UiMessage,
  UiMessageType,
  UiToolMessage,
} from '@/types/chat';
import { BASE_URL } from '@/environment';
import ApiService from '@/shared/services/api.service';

interface ConversationState {
  messages: UiMessage[];
  isGenerating: boolean;
  error: string | null;
  cancelToken: AbortController | null;
}

function initDefaultConversationState(): ConversationState {
  return {
    messages: [],
    isGenerating: false,
    error: null,
    cancelToken: null,
  };
}

interface ChatState {
  chatIsOpen: boolean;
  chatIsExpanded: boolean;
  currentConversationUuid: string;
  conversationStateByUuid: Record<string, ConversationState>;
}

const DEFAULT_STATE: ChatState = {
  chatIsOpen: false,
  chatIsExpanded: false,
  currentConversationUuid: crypto.randomUUID(),
  conversationStateByUuid: {},
};

export default {
  state: DEFAULT_STATE,
  mutations: {
    OPEN_CHAT(state: ChatState) {
      state.chatIsOpen = true;
    },
    CLOSE_CHAT(state: ChatState) {
      state.chatIsOpen = false;
    },
    TOGGLE_CHAT(state: ChatState) {
      state.chatIsOpen = !state.chatIsOpen;
    },
    TOGGLE_CHAT_EXPANDED(state: ChatState) {
      state.chatIsExpanded = !state.chatIsExpanded;
    },
    SET_CURRENT_CONVERSATION_UUID(
      state: ChatState,
      { conversationUuid }: { conversationUuid: string },
    ) {
      state.currentConversationUuid = conversationUuid;
    },
    SET_MESSAGES_FOR_CONVERSATION_UUID(
      state,
      {
        conversationUuid,
        messages,
      }: { conversationUuid: string; messages: UiMessage[] },
    ) {
      const existingState =
        state.conversationStateByUuid[conversationUuid] ??
        initDefaultConversationState();
      state.conversationStateByUuid[conversationUuid] = {
        ...existingState,
        messages,
      };
    },
    SET_IS_GENERATING_FOR_CONVERSATION_UUID(
      state: ChatState,
      {
        conversationUuid,
        isGenerating,
      }: { conversationUuid: string; isGenerating: boolean },
    ) {
      const existingState =
        state.conversationStateByUuid[conversationUuid] ??
        initDefaultConversationState();
      state.conversationStateByUuid[conversationUuid] = {
        ...existingState,
        isGenerating,
      };
    },
    SET_ERROR_FOR_CONVERSATION_UUID(
      state: ChatState,
      {
        conversationUuid,
        error,
      }: { conversationUuid: string; error: string | null },
    ) {
      const existingState =
        state.conversationStateByUuid[conversationUuid] ??
        initDefaultConversationState();
      state.conversationStateByUuid[conversationUuid] = {
        ...existingState,
        error,
      };
    },
    SET_CANCEL_TOKEN_FOR_CONVERSATION_UUID(
      state: ChatState,
      {
        conversationUuid,
        cancelToken,
      }: { conversationUuid: string; cancelToken: AbortController | null },
    ) {
      const existingState =
        state.conversationStateByUuid[conversationUuid] ??
        initDefaultConversationState();
      state.conversationStateByUuid[conversationUuid] = {
        ...existingState,
        cancelToken,
      };
    },
  },
  getters: {
    chatIsOpen: (state: ChatState) => state.chatIsOpen,
    chatIsExpanded: (state: ChatState) => state.chatIsExpanded,
    currentConversationUuid: (state: ChatState) =>
      state.currentConversationUuid,
    getStateForConversationUuid:
      (state: ChatState) => (conversationUuid: string) =>
        state.conversationStateByUuid[conversationUuid],
    getMessagesForConversationUuid:
      (state: ChatState) => (conversationUuid: string) => {
        const conversationState =
          state.conversationStateByUuid[conversationUuid];
        return conversationState?.messages ?? [];
      },
    getIsGeneratingForConversationUuid:
      (state: ChatState) => (conversationUuid: string) => {
        const conversationState =
          state.conversationStateByUuid[conversationUuid];
        return conversationState?.isGenerating ?? false;
      },
    getErrorForConversationUuid:
      (state: ChatState) => (conversationUuid: string) => {
        const conversationState =
          state.conversationStateByUuid[conversationUuid];
        return conversationState?.error ?? null;
      },
    getCancelTokenForConversationUuid:
      (state: ChatState) => (conversationUuid: string) => {
        const conversationState =
          state.conversationStateByUuid[conversationUuid];
        return conversationState?.cancelToken ?? null;
      },
  },
  actions: {
    openChat(context) {
      context.commit('OPEN_CHAT');
    },
    toggleChat(context) {
      context.commit('TOGGLE_CHAT');
    },
    closeChat(context) {
      context.commit('CLOSE_CHAT');
    },
    toggleChatExpanded(context) {
      context.commit('TOGGLE_CHAT_EXPANDED');
    },
    setCurrentConversationUuid(
      context,
      { conversationUuid }: { conversationUuid: string },
    ) {
      context.commit('SET_CURRENT_CONVERSATION_UUID', { conversationUuid });
    },
    createNewConversationUuid(context) {
      context.commit('SET_CURRENT_CONVERSATION_UUID', {
        conversationUuid: crypto.randomUUID(),
      });
    },
    async internalCreateConversationMessageAndCacheResponseStream(
      context,
      {
        conversationUuid,
        userMessageContent,
        contextCustomerUuids,
        contextDocumentIds,
      }: {
        conversationUuid: string;
        userMessageContent: string;
        contextCustomerUuids: string[];
        contextDocumentIds: string[];
      },
    ) {
      if (
        !conversationUuid ||
        !userMessageContent ||
        !contextCustomerUuids ||
        !contextDocumentIds
      ) {
        throw new Error('Missing required parameters');
      }

      const cancelToken = new AbortController();
      // Set cancel token on state so Chat components can access it, for example
      // when clicking the "Stop Generation" button
      context.commit('SET_CANCEL_TOKEN_FOR_CONVERSATION_UUID', {
        conversationUuid,
        cancelToken,
      });

      const response = await fetch(
        `${BASE_URL}api/v2/agents/chat/conversations/${conversationUuid}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            'X-CSRFToken': ApiService.token || '',
          },
          credentials: 'include',
          body: JSON.stringify({
            user_prompt: userMessageContent,
            context_customer_uuids: contextCustomerUuids.join(','),
            context_document_ids: contextDocumentIds.join(','),
          }),
          // Pass the abort signal so the caller can cancel the request
          signal: cancelToken.signal,
        },
      );

      if (!response.ok) {
        const errorJson = await response.json();
        throw new Error(errorJson.message);
      }

      const decoder = new TextDecoder('utf-8');
      const reader = response.body.getReader();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE event chunks separated by double newlines
        let boundaryIndex;

        while ((boundaryIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, boundaryIndex);
          buffer = buffer.slice(boundaryIndex + 2);

          // Check for stream completion
          if (rawEvent.startsWith('event: done')) {
            // Finish reading
            reader.cancel();
            break;
          }

          // Extract data lines (can be multi-line)
          const dataLines = rawEvent
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.replace(/^data:\s*/, ''));

          if (!dataLines.length) continue;

          const dataString = dataLines.join('\n').trim();
          const chunk = JSON.parse(dataString);

          // The response will stream chunks of data, with types defined in
          // the backend. As each chunk comes in, handle it appropriately
          // and update the UI.

          if (
            chunk.type === ChatAgentResponseChunkType.AssistantResponseStart
          ) {
            // If content starts with leading newlines, trim them
            const trimmedContent = chunk.content.replace(/^\n+/, '');
            const assistantMessage: UiAssistantMessage = {
              type: UiMessageType.AssistantMessage,
              content: trimmedContent,
            };

            let updatedMessages: UiMessage[] = [
              ...context.getters.getMessagesForConversationUuid(
                conversationUuid,
              ),
              assistantMessage,
            ];
            context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
              conversationUuid,
              messages: updatedMessages,
            });
          } else if (
            chunk.type === ChatAgentResponseChunkType.AssistantResponseDelta
          ) {
            let updatedMessages: UiMessage[] = [
              ...context.getters.getMessagesForConversationUuid(
                conversationUuid,
              ),
            ];
            // If there is no ongoing assistant message, create a new one
            if (
              updatedMessages.length === 0 ||
              updatedMessages[updatedMessages.length - 1].type !==
                UiMessageType.AssistantMessage
            ) {
              const assistantMessage: UiAssistantMessage = {
                type: UiMessageType.AssistantMessage,
                content: chunk.content_delta,
              };
              updatedMessages = [...updatedMessages, assistantMessage];
            }

            const latestAssistantMessage = updatedMessages[
              updatedMessages.length - 1
            ] as UiAssistantMessage;
            latestAssistantMessage.content = `${latestAssistantMessage.content}${chunk.content_delta}`;
            // Replace the latest assistant message with the updated one
            updatedMessages[updatedMessages.length - 1] =
              latestAssistantMessage;
            // Update state
            context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
              conversationUuid,
              messages: updatedMessages,
            });
          } else if (chunk.type === ChatAgentResponseChunkType.ToolCall) {
            const uiToolMessage: UiToolMessage = {
              type: UiMessageType.ToolMessage,
              toolCallId: chunk.tool_call_id,
              toolName: chunk.tool_name,
              args: chunk.args,
            };
            const updatedMessages: UiMessage[] = [
              ...context.getters.getMessagesForConversationUuid(
                conversationUuid,
              ),
              uiToolMessage,
            ];
            context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
              conversationUuid,
              messages: updatedMessages,
            });
          } else if (chunk.type === ChatAgentResponseChunkType.ToolResult) {
            const updatedMessages = [
              ...context.getters.getMessagesForConversationUuid(
                conversationUuid,
              ),
            ];
            // Find the existing UiToolMessage with the same tool call ID
            // and update the result
            const existingToolMessage = updatedMessages.find(
              (message) =>
                message.type === UiMessageType.ToolMessage &&
                message.toolCallId === chunk.tool_call_id,
            ) as UiToolMessage;
            if (existingToolMessage) {
              existingToolMessage.result = chunk.result;
            }
            context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
              conversationUuid,
              messages: updatedMessages,
            });
          } else if (chunk.type === ChatAgentResponseChunkType.Error) {
            const updatedMessages: UiMessage[] = [
              ...context.getters.getMessagesForConversationUuid(
                conversationUuid,
              ),
              {
                type: UiMessageType.Error,
                content: chunk,
              },
            ];
            context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
              conversationUuid,
              messages: updatedMessages,
            });
          }
        }
      }
    },
    // When a component wants to submit a conversation message, this is the
    // function to use. It wraps the internal logic for calling the API and
    // caching the response. All state including messages, isGenerating, error,
    // cancelToken is stored in the ChatStore based on the conversationUuid and
    // can be accessed from any component.
    async submitConversationMessage(
      context,
      {
        conversationUuid,
        userMessageContent,
        contextCustomerUuids,
        contextDocumentIds,
      }: {
        conversationUuid: string;
        userMessageContent: string;
        contextCustomerUuids: string[];
        contextDocumentIds: string[];
      },
    ) {
      context.commit('SET_IS_GENERATING_FOR_CONVERSATION_UUID', {
        conversationUuid,
        isGenerating: true,
      });

      // Optimistically add user message for display before API call
      const existingMessages =
        context.getters.getMessagesForConversationUuid(conversationUuid);
      context.commit('SET_MESSAGES_FOR_CONVERSATION_UUID', {
        conversationUuid,
        messages: [
          ...existingMessages,
          {
            type: UiMessageType.UserMessage,
            content: userMessageContent,
          },
        ],
      });

      context.commit('SET_ERROR_FOR_CONVERSATION_UUID', {
        conversationUuid,
        error: null,
      });

      try {
        await context.dispatch(
          'internalCreateConversationMessageAndCacheResponseStream',
          {
            conversationUuid,
            userMessageContent,
            contextCustomerUuids: contextCustomerUuids,
            contextDocumentIds: contextDocumentIds,
          },
        );
      } catch (error) {
        // Ignore abort errors triggered by stopGeneration
        if (error.name !== 'AbortError') {
          context.commit('SET_ERROR_FOR_CONVERSATION_UUID', {
            conversationUuid,
            error: error.message,
          });
        }
      } finally {
        context.commit('SET_IS_GENERATING_FOR_CONVERSATION_UUID', {
          conversationUuid,
          isGenerating: false,
        });
        context.commit('SET_CANCEL_TOKEN_FOR_CONVERSATION_UUID', {
          conversationUuid,
          cancelToken: null,
        });
      }
    },
  },
};
